class LoopingCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-carousel-viewport]');
    this.track = this.querySelector('[data-carousel-track]');
    this.previousButton = this.querySelector('[data-carousel-previous]');
    this.nextButton = this.querySelector('[data-carousel-next]');

    if (!this.viewport || !this.track) return;

    this.originalCount = Number.parseInt(this.dataset.carouselCount || '0', 10);
    this.cloneCount = Number.parseInt(this.dataset.carouselClones || '0', 10);
    this.buildTrack();

    this.activeIndex = this.cloneCount;
    this.dragStartX = 0;
    this.dragOffset = 0;
    this.isDragging = false;
    this.isAnimating = false;
    this.transitionDuration = 240;

    this.warmImages();

    this.handlePrevious = () => this.moveBy(-1);
    this.handleNext = () => this.moveBy(1);
    this.handleResize = () => this.recalculate();
    this.handleTransitionEnd = () => this.onTransitionEnd();
    this.handlePointerDown = (event) => this.onPointerDown(event);
    this.handlePointerMove = (event) => this.onPointerMove(event);
    this.handlePointerUp = () => this.onPointerUp();

    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.viewport.addEventListener('pointerdown', this.handlePointerDown);
    this.viewport.addEventListener('pointermove', this.handlePointerMove);
    this.viewport.addEventListener('pointerup', this.handlePointerUp);
    this.viewport.addEventListener('pointercancel', this.handlePointerUp);
    this.viewport.addEventListener('lostpointercapture', this.handlePointerUp);
    this.track.addEventListener('transitionend', this.handleTransitionEnd);

    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.viewport);

    this.recalculate();
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.viewport?.removeEventListener('pointerdown', this.handlePointerDown);
    this.viewport?.removeEventListener('pointermove', this.handlePointerMove);
    this.viewport?.removeEventListener('pointerup', this.handlePointerUp);
    this.viewport?.removeEventListener('pointercancel', this.handlePointerUp);
    this.viewport?.removeEventListener('lostpointercapture', this.handlePointerUp);
    this.track?.removeEventListener('transitionend', this.handleTransitionEnd);
    this.resizeObserver?.disconnect();
  }

  warmImages() {
    const images = this.querySelectorAll('img');

    images.forEach((image) => {
      image.loading = 'eager';
      image.decoding = 'async';
    });
  }

  buildTrack() {
    const existingClones = this.track.querySelectorAll('[data-carousel-clone]');
    existingClones.forEach((clone) => clone.remove());

    const originals = Array.from(this.track.children);
    const edgeCount = Math.min(this.cloneCount, originals.length);

    const prependClones = originals.slice(-edgeCount).map((item) => this.createClone(item));
    const appendClones = originals.slice(0, edgeCount).map((item) => this.createClone(item));

    prependClones.reverse().forEach((clone) => {
      this.track.prepend(clone);
    });

    appendClones.forEach((clone) => {
      this.track.append(clone);
    });

    this.items = Array.from(this.track.children);
  }

  createClone(item) {
    const clone = item.cloneNode(true);
    clone.dataset.carouselClone = 'true';
    clone.removeAttribute('id');
    clone.setAttribute('aria-hidden', 'true');
    return clone;
  }

  get visibleCount() {
    const firstItem = this.items[0];
    if (!firstItem) return 1;

    const viewportWidth = this.viewport.clientWidth;
    const cardWidth = firstItem.getBoundingClientRect().width;

    if (!cardWidth) return 1;

    return Math.max(1, Math.round(viewportWidth / cardWidth));
  }

  get canLoop() {
    return this.originalCount > 1 && this.cloneCount > 0 && this.step > 0;
  }

  recalculate() {
    const firstItem = this.items[0];
    if (!firstItem) return;

    this.itemWidth = firstItem.getBoundingClientRect().width;
    const computedTrackStyle = getComputedStyle(this.track);
    this.gap = parseFloat(computedTrackStyle.columnGap || computedTrackStyle.gap || '0') || 0;
    this.step = this.itemWidth + this.gap;

    if (!this.canLoop) {
      this.setAttribute('static', '');
      this.activeIndex = 0;
      this.setControlsDisabled(true);
      this.setTranslate(false);
      return;
    }

    this.removeAttribute('static');
    this.setControlsDisabled(false);

    if (this.activeIndex < this.cloneCount || this.activeIndex >= this.cloneCount + this.originalCount) {
      this.activeIndex = this.cloneCount;
    }

    this.setTranslate(false);
  }

  setControlsDisabled(disabled) {
    if (this.previousButton) this.previousButton.disabled = disabled;
    if (this.nextButton) this.nextButton.disabled = disabled;
  }

  setTranslate(animate, offset = 0) {
    this.track.style.transition = animate
      ? `transform ${this.transitionDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`
      : 'none';
    this.track.style.transform = `translate3d(${-1 * ((this.activeIndex * this.step) - offset)}px, 0, 0)`;
  }

  moveBy(delta) {
    if (!this.canLoop || this.isDragging) return;

    this.activeIndex += delta;
    this.isAnimating = true;
    this.setTranslate(true);
  }

  onTransitionEnd() {
    if (!this.canLoop) {
      this.isAnimating = false;
      return;
    }

    while (this.activeIndex < this.cloneCount) {
      this.activeIndex += this.originalCount;
      this.setTranslate(false);
    }

    while (this.activeIndex >= this.cloneCount + this.originalCount) {
      this.activeIndex -= this.originalCount;
      this.setTranslate(false);
    }

    this.isAnimating = false;
  }

  onPointerDown(event) {
    if (!this.canLoop || this.isAnimating) return;
    if (event.target instanceof Element && event.target.closest('[data-carousel-previous], [data-carousel-next], a, button, input, textarea, select, label')) {
      return;
    }

    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragOffset = 0;
    this.setAttribute('dragging', '');
    event.preventDefault();
    this.viewport.setPointerCapture(event.pointerId);
    this.track.style.transition = 'none';
  }

  onPointerMove(event) {
    if (!this.isDragging) return;

    this.dragOffset = event.clientX - this.dragStartX;
    this.setTranslate(false, this.dragOffset);
  }

  onPointerUp() {
    if (!this.isDragging) return;

    const threshold = Math.max(this.itemWidth * 0.18, 36);
    const delta = this.dragOffset;

    this.isDragging = false;
    this.removeAttribute('dragging');
    this.dragOffset = 0;

    if (Math.abs(delta) > threshold) {
      const slideShift = Math.max(1, Math.round(Math.abs(delta) / this.step));
      this.activeIndex += delta < 0 ? slideShift : -slideShift;
      this.isAnimating = true;
      this.setTranslate(true);
      return;
    }

    this.setTranslate(true);
  }
}

if (!customElements.get('looping-carousel')) {
  customElements.define('looping-carousel', LoopingCarousel);
}
